import React, { useMemo, useState } from 'react';
import { ChargeMessageDto, InvoiceMessageDto } from 'src/generated/services/TFinancialApi';
import { EditableTable, EditableTableColumn, EditableTableGroup } from 'src/presentations/components/organisms/editableTable/EditableTable';
import 'src/presentations/components/organisms/editableTable/EditableTable.css';
import { ValidateInvoiceStep } from 'src/presentations/components/molecules/uploadInvoice/types';
import { ValidateNode } from 'src/use-cases/internalDashboard/useValidation';

interface ChargesTableProps {
  invoiceMessage: InvoiceMessageDto;
  onUpdate: (invoiceMessage: InvoiceMessageDto) => void;
  validateJson: ValidateNode<InvoiceMessageDto>;
  validateUpdatedJson: ValidateNode<InvoiceMessageDto>;
  ocrMessage: InvoiceMessageDto;
  activeStep: ValidateInvoiceStep;
}

interface GroupedCharge extends ChargeMessageDto {
  shipmentNumber: string;
}

export const ChargesTable = ({ invoiceMessage, onUpdate, validateJson, validateUpdatedJson, ocrMessage, activeStep }: ChargesTableProps) => {
  const groupedCharges = useMemo(() => {
    const groups: Record<string, GroupedCharge[]> = {};
    if (invoiceMessage.shipments) {
      invoiceMessage.shipments.forEach((shipment) => {
        if (shipment.shipmentNumber || shipment.shipmentNumber === '') {
          groups[shipment.shipmentNumber] = (shipment.charges || []).map((charge) => ({
            ...charge,
            shipmentNumber: shipment.shipmentNumber,
          }));
        }
      });
    }
    return groups;
  }, [invoiceMessage]);

  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => Object.keys(groupedCharges).reduce((acc, key) => ({ ...acc, [key]: true }), {}));

  const handleToggle = (shipmentNumber: string) => {
    setExpanded((prev) => ({ ...prev, [shipmentNumber]: !prev[shipmentNumber] }));
  };

  const handleAdd = (shipmentNumber: string) => {
    const updatedShipments =
      invoiceMessage.shipments?.map((shipment) => {
        if (shipment.shipmentNumber === shipmentNumber) {
          return {
            ...shipment,
            charges: [...(shipment.charges || []), { name: '', amountAfterTax: 0 }],
          };
        }
        return shipment;
      }) || [];
    onUpdate({
      ...invoiceMessage,
      shipments: updatedShipments,
    });
  };

  const handleDelete = (shipmentNumber: string, idx: number) => {
    const updatedShipments =
      invoiceMessage.shipments?.map((shipment) => {
        if (shipment.shipmentNumber === shipmentNumber) {
          return {
            ...shipment,
            charges: (shipment.charges || []).filter((_, i) => i !== idx),
          };
        }
        return shipment;
      }) || [];
    onUpdate({
      ...invoiceMessage,
      shipments: updatedShipments,
    });
  };

  const handleCellEdit = (shipmentNumber: string, idx: number, field: keyof ChargeMessageDto, value: any) => {
    const updatedShipments =
      invoiceMessage.shipments?.map((shipment) => {
        if (shipment.shipmentNumber === shipmentNumber) {
          const updatedCharges = [...(shipment.charges || [])];
          updatedCharges[idx] = {
            ...updatedCharges[idx],
            [field]: columns.find((col) => col.key === field)?.type === 'number' ? Number(value) || 0 : value,
          };
          return {
            ...shipment,
            charges: updatedCharges,
          };
        }
        return shipment;
      }) || [];
    onUpdate({
      ...invoiceMessage,
      shipments: updatedShipments,
    });
  };

  // Helper to get validation for a charge row
  const getChargeValidation = (shipmentNumber: string, chargeIdx: number, field: string) => {
    const shipmentIdx = invoiceMessage.shipments?.findIndex((s) => s.shipmentNumber === shipmentNumber) ?? -1;
    if (shipmentIdx === -1) return { error: false, helperText: '', updatedError: false, updatedHelperText: '', originalValue: undefined };
    const shipmentValidation = (validateJson?.shipments as any)?.value?.[shipmentIdx];
    const chargeValidation = (shipmentValidation?.charges as any)?.value?.[chargeIdx];
    const updatedChargeValidation = (validateUpdatedJson?.shipments as any)?.value?.[shipmentIdx]?.charges?.value?.[chargeIdx];
    const fieldValidation = chargeValidation?.[field];
    const updatedFieldValidation = updatedChargeValidation?.[field];
    let originalValue;
    if (ocrMessage) {
      const ocrShipment = ocrMessage.shipments?.find((s) => s.shipmentNumber === shipmentNumber);
      originalValue = ocrShipment?.charges?.[chargeIdx]?.[field];
    }
    return {
      error: !!fieldValidation && fieldValidation.isValid === false,
      helperText: fieldValidation?.exception?.error || '',
      updatedError: !!updatedFieldValidation && updatedFieldValidation.isValid === false,
      originalValue,
    };
  };

  const columns: EditableTableColumn[] = [
    { key: 'name', label: 'Charge Name', width: 300 },
    { key: 'code', label: 'Code', width: 120 },
    { key: 'gstCode', label: 'GST Code', width: 120 },
    { key: 'quantity', label: 'Quantity', width: 100 },
    { key: 'chargeUnit', label: 'Charge Unit', width: 120 },
    { key: 'currency', label: 'Currency', width: 100 },
    { key: 'unitPrice', label: 'Unit Price', width: 120 },
    { key: 'exchangeRate', label: 'Exchange Rate', width: 120 },
    { key: 'amountBeforeTax', label: 'Amount Before Tax', width: 150 },
    { key: 'taxAmount', label: 'Tax Amount', width: 120 },
    { key: 'taxPercentage', label: 'Tax Percentage', width: 120 },
    { key: 'discountAmount', label: 'Discount Amount', width: 150 },
    { key: 'amountAfterTax', label: 'Amount After Tax', width: 150 },
  ];

  const groups: EditableTableGroup[] = Object.entries(groupedCharges).map(([shipmentNumber, charges]) => ({
    key: shipmentNumber,
    label: `Shipment ${shipmentNumber} (${charges.length} charges)`,
    rows: charges.map((charge, idx) => {
      const rowWithValidation = { ...charge };
      columns.forEach((col) => {
        const v = getChargeValidation(shipmentNumber, idx, col.key);
        rowWithValidation[`${col.key}__error`] = v.error;
        rowWithValidation[`${col.key}__helperText`] = v.helperText;
        rowWithValidation[`${col.key}__updatedError`] = v.updatedError;
        rowWithValidation[`${col.key}__originalValue`] = v.originalValue;
      });
      return rowWithValidation;
    }),
    expanded: expanded[shipmentNumber],
    onToggle: () => handleToggle(shipmentNumber),
    onAdd: () => handleAdd(shipmentNumber),
  }));

  return (
    <EditableTable
      columns={columns}
      groups={groups}
      onDelete={handleDelete}
      onCellEdit={(groupKey, rowIdx, field, value) => handleCellEdit(groupKey, rowIdx, field as keyof ChargeMessageDto, value)}
      activeStep={activeStep}
    />
  );
};
